import React from 'react';

import NewThemeProvider from '../../../../providers/NewThemeProvider';
import { TabSubrouter } from '../../../tabs/v2/TabSubrouter';
import { useUnknownTabRedirect } from '../../../tabs/v2/useUnknownTabRedirect';
import { useActor } from '../../../hooks/useActor';
import { useAllow } from '../../../hooks/useAllow';
import { usePersona } from '../../../hooks/usePersona';

import { EditUserProfile } from './EditUserProfile';
import ChangePassword from './ChangePassword';
import { MultiFactor } from './MultiFactor';
import { EmailSignatures, SystemSignatures } from './signatures';

// The tab content keeps the "new" theme; the TabSubrouter tab bar itself must
// stay on the default app theme (it reads `config.colors` from it), so
// NewThemeProvider wraps only the content, not the whole subrouter. No top
// margin, so the content panel sits flush against the tab bar.
const tabContent = (node: React.ReactNode) => (
  <NewThemeProvider>{node}</NewThemeProvider>
);

export const Profile = () => {
  // Check if user has root:root permissions for team signatures
  const hasRootPermission = useAllow(['root:root']);
  const { basePath } = usePersona();

  // Both signature tabs talk to ds-docmessage, whose /v1/signatures and
  // /v1/system/signatures endpoints are @PreAuthorize("hasAnyRole(@COWBELL_ACCOUNT)").
  // Offering either tab to another persona only produces a guaranteed 403 on
  // mount, so gate on the account the caller is acting in (FIR-21141; same
  // shape as the agent-tones fix in OMNI-589). `useActor` rather than
  // `usePersona` so an in-app jump into, say, a policyholder account is
  // treated like a direct login there. root:root is a separate scope from
  // account:cowbell, so Team Signatures needs both.
  const { isCowbell } = useActor();

  // The first ("Profile") tab is always present and carries no path, so
  // TabSubrouter's `value={tab || null}` matches it on the bare /manage/me URL.
  const subroutes = [
    {
      label: 'Profile',
      render: () => tabContent(<EditUserProfile />),
    },
    {
      path: 'password',
      label: 'Password',
      render: () => tabContent(<ChangePassword />),
    },
    {
      path: 'mfa',
      label: 'MFA',
      render: () => tabContent(<MultiFactor />),
    },
    ...(isCowbell
      ? [
          {
            path: 'email-signatures',
            label: 'Email Signatures',
            render: () => tabContent(<EmailSignatures />),
          },
        ]
      : []),
    // Team signatures tab only for root users, and only inside Cowbell
    ...(hasRootPermission && isCowbell
      ? [
          {
            path: 'team-signatures',
            label: 'Team Signatures',
            render: () => tabContent(<SystemSignatures />),
          },
        ]
      : []),
  ];

  // A non-Cowbell user has both signature tabs omitted (and Team Signatures is
  // root-only on top of that). Redirect a deep link to one of those now-absent
  // segments back to the default Profile tab.
  const unknownTabRedirect = useUnknownTabRedirect(
    subroutes,
    `${basePath}/manage/me`
  );
  if (unknownTabRedirect) {
    return unknownTabRedirect;
  }

  return (
    <TabSubrouter baseUrl={`${basePath}/manage/me`} subroutes={subroutes} />
  );
};

export default Profile;
